import { DASHBOARD_METRIC_STATUS, DASHBOARD_STATUS } from './dashboardContract';

const MAX_NAME_LENGTH = 150;
const MAX_DESCRIPTION_LENGTH = 1000;
const MAX_PUBLICATION_NOTE_LENGTH = 1000;
const MAX_DELETION_REASON_LENGTH = 500;

export const getDashboardTemplateState = (templates = []) => {
  const systemTemplate =
    templates.find((template) => template.ownership === 'system') ?? null;
  const customerTemplates = templates.filter(
    (template) => template.ownership === 'customer'
  );

  return {
    customerTemplates,
    systemTemplate
  };
};

export const getDashboardTemplateStatusLabel = (template) => {
  if (template?.ownership === 'system') return 'System default';
  if (template?.dashboardStatus === DASHBOARD_STATUS.ARCHIVED) {
    return 'Archived';
  }
  if (template?.dashboardStatus === DASHBOARD_STATUS.PUBLISHED) {
    return template.draftVersion ? 'Published · Draft changes' : 'Published';
  }
  return 'Draft';
};

export const validateDashboardTemplateMetadata = ({
  description = '',
  name = ''
} = {}) => {
  const errors = {};
  const normalizedName = typeof name === 'string' ? name.trim() : '';
  const normalizedDescription =
    typeof description === 'string' ? description.trim() : '';

  if (!normalizedName) {
    errors.name = 'Dashboard name is required.';
  } else if (normalizedName.length > MAX_NAME_LENGTH) {
    errors.name = `Dashboard name cannot exceed ${MAX_NAME_LENGTH} characters.`;
  }

  if (normalizedDescription.length > MAX_DESCRIPTION_LENGTH) {
    errors.description = `Dashboard description cannot exceed ${MAX_DESCRIPTION_LENGTH} characters.`;
  }

  return {
    errors,
    isValid: Object.keys(errors).length === 0,
    values: {
      description: normalizedDescription || null,
      name: normalizedName
    }
  };
};

export const buildCreateDashboardTemplateRequest = (
  values,
  cloneSystemDefault
) => ({
  ...values,
  ...(cloneSystemDefault ? { cloneFrom: 'system_default' } : {})
});

export const validateDashboardPublicationNote = (publicationNote = '') => {
  const normalizedNote =
    typeof publicationNote === 'string' ? publicationNote.trim() : '';
  let error = null;

  if (!normalizedNote) {
    error = 'A publication note is required.';
  } else if (normalizedNote.length > MAX_PUBLICATION_NOTE_LENGTH) {
    error = `The publication note cannot exceed ${MAX_PUBLICATION_NOTE_LENGTH} characters.`;
  }

  return {
    error,
    isValid: !error,
    publicationNote: normalizedNote
  };
};

export const validateDashboardDeletionReason = (reason = '') => {
  const normalizedReason = typeof reason === 'string' ? reason.trim() : '';
  let error = null;

  if (!normalizedReason) {
    error = 'A dashboard deletion reason is required.';
  } else if (normalizedReason.length > MAX_DELETION_REASON_LENGTH) {
    error = `The dashboard deletion reason cannot exceed ${MAX_DELETION_REASON_LENGTH} characters.`;
  }

  return {
    error,
    isValid: !error,
    reason: normalizedReason
  };
};

export const getDashboardDeletedRestoreConflicts = (error) => {
  const dashboardError = error?.errors?.find(
    (candidate) => candidate.code === 'dashboard_deleted_restore_conflict'
  );
  const conflicts = dashboardError?.details?.conflicts;

  return Array.isArray(conflicts)
    ? conflicts.filter(
        (conflict) =>
          (conflict?.scopeType === 'entity' ||
            conflict?.scopeType === 'industry_type') &&
          typeof conflict?.scopeId === 'string'
      )
    : [];
};

export const getDashboardPublicationReadiness = (draftVersion) => {
  if (!draftVersion) {
    return {
      activeMetricCount: 0,
      isReady: false,
      issues: ['Create or restore an editable draft before publishing.'],
      visualizationCount: 0
    };
  }

  const metrics = Array.isArray(draftVersion.metrics)
    ? draftVersion.metrics
    : [];
  const visualizations = Array.isArray(draftVersion.visualizations)
    ? draftVersion.visualizations
    : [];
  const activeMetricIds = new Set(
    metrics
      .filter(
        (metric) => metric.lifecycleStatus === DASHBOARD_METRIC_STATUS.ACTIVE
      )
      .map((metric) => metric.metricDefinitionId)
  );
  const issues = [];

  if (!activeMetricIds.size) {
    issues.push('Add at least one active metric before publishing.');
  }

  if (
    visualizations.some(
      (visualization) =>
        !Array.isArray(visualization.series) ||
        visualization.series.length === 0
    )
  ) {
    issues.push('Every graph must contain at least one metric series.');
  }

  if (
    visualizations.some((visualization) =>
      (visualization.series ?? []).some(
        (series) => !activeMetricIds.has(series.metricDefinitionId)
      )
    )
  ) {
    issues.push('Graph series can only reference active metrics.');
  }

  return {
    activeMetricCount: activeMetricIds.size,
    isReady: issues.length === 0,
    issues,
    visualizationCount: visualizations.length
  };
};
